import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Typography, useMediaQuery } from "@mui/material";
import { OnboardingArrowRightIcon } from "../../../assets/icon";
import images from "../../../assets/images";
import { DotsIndicator } from "../dots-indicator-onboarding/dots-indicator-onboarding";
import { motion } from "framer-motion";
import { SkeletonLoader } from "../../skeleton/skeleton";

interface StatisticOnboardingProps {
  handleNextPage: () => void;
  handlePrevPage: () => void;
  handleSkip: () => void;
}

const itemVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: (custom: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: custom * 0.3, duration: 0.5 },
  }),
};

export const StatisticOnboarding: React.FC<StatisticOnboardingProps> = ({
  handlePrevPage,
  handleNextPage,
  handleSkip,
}) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const isCompactLandscape = useMediaQuery(
    "(orientation: landscape) and (max-width: 1366px)",
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [chartLoaded, setChartLoaded] = useState(false);
  const [certLoaded, setCertLoaded] = useState(false);
  const [boardLoaded, setBoardLoaded] = useState(false);
  const [timeLoaded, setTimeLoaded] = useState(false);

  useEffect(() => {
    const checkSize = () => {
      if (contentRef.current && containerRef.current) {
        if (isCompactLandscape) {
          setScale(1);
          return;
        }

        const containerHeight = containerRef.current.clientHeight;
        const containerWidth = containerRef.current.clientWidth;
        const contentHeight = contentRef.current.scrollHeight;
        const contentWidth = contentRef.current.scrollWidth;
        const safetyMargin = isCompactLandscape ? 0.96 : 0.98;
        const heightScale = containerHeight / contentHeight;
        const widthScale = containerWidth / contentWidth;
        const fittedScale = Math.min(heightScale, widthScale) * safetyMargin;
        const minScale = isMobile ? 0 : 0.8;
        setScale(Math.max(Math.min(fittedScale, 1), minScale));
      }
    };

    checkSize();
    window.addEventListener("resize", checkSize);
    window.addEventListener("orientationchange", checkSize);
    const timer = setTimeout(checkSize, 300);
    return () => {
      window.removeEventListener("resize", checkSize);
      window.removeEventListener("orientationchange", checkSize);
      clearTimeout(timer);
    };
  }, [isCompactLandscape, isMobile]);

  return (
    <Box
      ref={containerRef}
      sx={{
        overflowY: isCompactLandscape ? "auto" : isMobile ? "hidden" : "auto",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        height: "100dvh",
        alignItems: "center",
        justifyContent: isCompactLandscape ? "flex-start" : "space-around",
      }}
    >
      <Box
        ref={contentRef}
        sx={{
          transform: `scale(${scale})`,
          transformOrigin: "center center",
          width: "100%",
        }}
      >
        <Box
          sx={{
            margin: "8px",
            background: "linear-gradient(to bottom, #28C9DC, #8957CF)",
            borderRadius: "30px",
            width: { xs: "374px", md: "460px" },
            height: { xs: "315px", md: "310px" },
            position: "relative",
            overflow: "hidden",
          }}
        >
          {!chartLoaded && (
            <SkeletonLoader
              variant="rectangular"
              width={190}
              height={132}
              sx={{
                position: "absolute",
                left: { xs: 14, md: 57 },
                top: { xs: 16, md: 13 },
              }}
            />
          )}
          <Box
            component={motion.img}
            src={images.OnboardingStatisticCharts}
            alt="Statistic Charts"
            onLoad={() => setChartLoaded(true)}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={chartLoaded ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.2, duration: 0.5 }}
            sx={{
              position: "absolute",
              width: "190px",
              height: "132px",
              left: { xs: 14, md: 57 },
              top: { xs: 16, md: 13 },
              display: chartLoaded ? "block" : "none",
            }}
          />

          {!certLoaded && (
            <SkeletonLoader
              variant="rectangular"
              width={190}
              height={132}
              sx={{
                position: "absolute",
                left: { xs: 14, md: 57 },
                bottom: { xs: 16, md: 13 },
              }}
            />
          )}
          <Box
            component={motion.img}
            src={images.OnboardingStatistictCertificate}
            alt="Statistic Certificate"
            onLoad={() => setCertLoaded(true)}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={certLoaded ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.4, duration: 0.5 }}
            sx={{
              position: "absolute",
              width: "190px",
              height: "132px",
              left: { xs: 14, md: 57 },
              bottom: { xs: 16, md: 13 },
              display: certLoaded ? "block" : "none",
            }}
          />
          {!boardLoaded && (
            <SkeletonLoader
              variant="rectangular"
              width={144}
              height={80}
              sx={{
                position: "absolute",
                right: { xs: 12, md: 57 },
                top: { xs: 103, md: 100 },
              }}
            />
          )}
          <Box
            component={motion.img}
            src={images.OnboardingStatisticLeaderboard}
            alt="Statistic Leaderboard"
            onLoad={() => setBoardLoaded(true)}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={boardLoaded ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.6, duration: 0.5 }}
            sx={{
              position: "absolute",
              width: "144px",
              height: "80px",
              right: { xs: 12, md: 57 },
              top: { xs: 103, md: 100 },
              display: boardLoaded ? "block" : "none",
            }}
          />
          {!timeLoaded && (
            <SkeletonLoader
              variant="rectangular"
              width={144}
              height={80}
              sx={{
                position: "absolute",
                right: { xs: 12, md: 57 },
                bottom: { xs: 48, md: 45 },
              }}
            />
          )}
          <Box
            component={motion.img}
            src={images.OnboardingStatisticTime}
            alt="Statistic Time"
            onLoad={() => setTimeLoaded(true)}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={timeLoaded ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.8, duration: 0.5 }}
            sx={{
              position: "absolute",
              width: "144px",
              height: "80px",
              right: { xs: 12, md: 57 },
              bottom: { xs: 48, md: 45 },
              display: timeLoaded ? "block" : "none",
            }}
          />
        </Box>

        <Box
          sx={{
            maxWidth: { xs: "345px", md: "405px" },
            margin: "0 auto",
            marginTop: { xs: "5px", md: "10px" },
          }}
        >
          <Typography
            sx={{
              fontSize: isCompactLandscape
                ? { xs: "26px", md: "30px" }
                : { xs: "30px", md: "40px" },
              fontWeight: 700,
              textAlign: "center",
              marginBottom: { xs: "5px", md: "10px" },
            }}
          >
            Personal Statistic
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Box
              component={motion.div}
              variants={itemVariants}
              initial="hidden"
              animate="visible"
              custom={1}
              sx={{
                display: "flex",
                background: "#D6FBFF",
                padding: "6px",
                borderRadius: "16px",
                alignItems: "center",
                gap: "16px",
              }}
            >
              <Typography
                sx={{
                  fontSize: isCompactLandscape ? "16px" : "20px",
                  background: "#FFFFFF",
                  padding: isCompactLandscape ? "8px 10px" : "12px 14px",
                  borderRadius: "10px",
                }}
              >
                🏆
              </Typography>
              <Typography
                sx={{
                  color: "#092346",
                  fontSize: isCompactLandscape ? "13px" : "16px",
                  fontWeight: 600,
                }}
              >
                Earn Certificates & Achievment
              </Typography>
            </Box>
            <Box
              component={motion.div}
              variants={itemVariants}
              initial="hidden"
              animate="visible"
              custom={2}
              sx={{
                display: "flex",
                background: "#D6FBFF",
                padding: "6px",
                borderRadius: "16px",
                alignItems: "center",
                gap: "16px",
              }}
            >
              <Typography
                sx={{
                  fontSize: isCompactLandscape ? "16px" : "20px",
                  background: "#FFFFFF",
                  padding: isCompactLandscape ? "8px 10px" : "12px 14px",
                  borderRadius: "10px",
                }}
              >
                🎯
              </Typography>
              <Typography
                sx={{
                  color: "#092346",
                  fontSize: isCompactLandscape ? "13px" : "16px",
                  fontWeight: 600,
                }}
              >
                Track your progress & Growth
              </Typography>
            </Box>
            <Box
              component={motion.div}
              variants={itemVariants}
              initial="hidden"
              animate="visible"
              custom={3}
              sx={{
                display: "flex",
                background: "#D6FBFF",
                padding: "6px",
                borderRadius: "16px",
                alignItems: "center",
                gap: "16px",
              }}
            >
              <Typography
                sx={{
                  fontSize: isCompactLandscape ? "16px" : "20px",
                  background: "#FFFFFF",
                  padding: isCompactLandscape ? "8px 10px" : "12px 14px",
                  borderRadius: "10px",
                }}
              >
                ⭐
              </Typography>
              <Typography
                sx={{
                  color: "#092346",
                  fontSize: isCompactLandscape ? "13px" : "16px",
                  fontWeight: 600,
                }}
              >
                Compete for the championship
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginTop: { xs: "10px", sm: "14px" },
              marginBottom: { xs: "8px", sm: "12px" },
            }}
          >
            <Button
              onClick={handleNextPage}
              sx={{
                background: "linear-gradient(90deg, #28C9DC 0%, #8957CF 100%)",
                color: "#FFFFFF",
                textTransform: "none",
                fontWeight: "bold",
                borderRadius: "999px",
                px: isCompactLandscape ? "22px" : "32px",
                py: isCompactLandscape ? "9px" : "16px",
                "&:hover": {
                  background:
                    "linear-gradient(90deg, #20B6C8 0%, #7C4ABF 100%)",
                },
              }}
            >
              Next
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              width: "100%",
              marginTop: { xs: "10px", sm: "12px" },
              alignItems: "center",
              marginBottom: { xs: "8px", sm: "10px" },
              paddingX: { xs: "8px", sm: "12px" },
            }}
          >
            <OnboardingArrowRightIcon
              style={{ width: "30px", cursor: "pointer" }}
              onClick={handlePrevPage}
            />
            <DotsIndicator activeIndex={5} />
            <Typography
              onClick={handleSkip}
              sx={{
                fontSize: { xs: "14px", sm: "16px" },
                color: "#B6BAD5",
                cursor: "pointer",
              }}
            >
              Skip
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default StatisticOnboarding;
